import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { useAuth } from "./AuthContext.jsx";

const STORAGE_KEY = "catalog_cart_state";

const initialState = {
  items: [],
  // Titles of entries taken out because they can no longer be bought (sold,
  // pulled, made showcase-only, deleted), for the notice on the cart and
  // checkout pages. Cleared by DISMISS_REMOVED; never persisted.
  removed: [],
};

// How often an open tab re-checks its cart against the database, so a piece
// that sells while someone browses leaves their cart then, not at checkout.
const REVALIDATE_INTERVAL_MS = 60000;

/**
 * True if the cart entry’s item is considered available.
 * Requirements: item exists, state is LIVE, not sold, and is_available is not false.
 */
function isEntryAvailable(entry) {
  if (!entry || !entry.item || entry.gone) {
    return false;
  }
  const item = entry.item;
  if (item.state !== "LIVE") return false;
  if (item.sold === true) return false;
  if (item.is_available === false) return false;
  // Showcase pieces are display-only: checkout refuses them too.
  if (item.showcase) return false;
  return true;
}

const entryTitle = (entry) => entry?.item?.title || "An item";

// Keeps the purchasable candidates as the cart and records the rest in
// `removed`, so the reader is told rather than finding a line gone.
function keepAvailable(state, candidates) {
  const items = candidates.filter(isEntryAvailable);
  const dropped = candidates.filter((entry) => !isEntryAvailable(entry));
  if (!dropped.length) {
    return { ...state, items };
  }
  const removed = [...new Set([...(state.removed || []), ...dropped.map(entryTitle)])];
  return { ...state, items, removed };
}

function cartReducer(state, action) {
  switch (action.type) {
    case "ADD_ITEM": {
      const item = action.payload;
      const size = action.size || null;
      const color = action.color || null;
      if (!item || typeof item.id === "undefined" || item.id === null) {
        return state;
      }
      const existing = state.items.find(
        (entry) => entry.id === item.id && entry.size === size && entry.color === color,
      );
      if (existing) {
        return {
          ...state,
          items: state.items.map((entry) =>
            entry.id === item.id && entry.size === size && entry.color === color
              ? { ...entry, quantity: 1 }
              : entry
          ),
        };
      }
      const next = {
        ...state,
        items: [
          ...state.items,
          {
            id: item.id,
            item,
            size,
            color,
            quantity: 1,
            addedAt: Date.now(),
          },
        ],
      };
      return next;
    }
    case "REMOVE_ITEM": {
      const { itemId, size, color } = action.payload;
      if (itemId === null || itemId === undefined) {
        return state;
      }
      return {
        ...state,
        items: state.items.filter(
          (entry) => !(entry.id === itemId && entry.size === size && entry.color === color),
        ),
      };
    }
    case "UPDATE_QUANTITY": {
      const { itemId, size, color, quantity } = action.payload;
      if (quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(
            (entry) => !(entry.id === itemId && entry.size === size && entry.color === color),
          ),
        };
      }
      return {
        ...state,
        items: state.items.map((entry) =>
          entry.id === itemId && entry.size === size && entry.color === color
            ? { ...entry, quantity: 1 }
            : entry
        ),
      };
    }
    case "CLEAR_CART": {
      if (!state.items.length) {
        return state;
      }
      return { ...state, items: [] };
    }
    case "RESTORE_STATE": {
      if (!action.payload || !Array.isArray(action.payload.items)) {
        return state;
      }
      const restored = action.payload.items
        .map((entry) => ({
          id: entry.id,
          item: entry.item,
          size: entry.size || null,
          color: entry.color || null,
          quantity: 1,
          addedAt: entry.addedAt ?? Date.now(),
        }));
      return keepAvailable(state, restored);
    }
    case "REFRESH_ITEM": {
      const item = action.payload;
      if (!item || typeof item.id === "undefined" || item.id === null) {
        return state;
      }
      const hasEntry = state.items.some((entry) => entry.id === item.id);
      if (!hasEntry) {
        return state;
      }
      // Swap in the fresh server copy (price, availability) and drop the
      // entry entirely if the item is no longer purchasable.
      return keepAvailable(
        state,
        state.items.map((entry) => (entry.id === item.id ? { ...entry, item } : entry)),
      );
    }
    case "REFRESH_ITEMS": {
      const fresh = Array.isArray(action.payload?.items) ? action.payload.items : [];
      const requested = new Set(action.payload?.requestedIds || []);
      if (!requested.size) {
        return state;
      }
      const freshById = new Map(
        fresh
          .filter((item) => item && item.id !== undefined && item.id !== null)
          .map((item) => [item.id, item]),
      );
      const items = state.items.map((entry) => {
        if (!requested.has(entry.id)) {
          return entry;
        }
        const item = freshById.get(entry.id);
        // Requested but not returned: the item no longer exists in the DB.
        return item ? { ...entry, item } : { ...entry, gone: true };
      });
      const next = keepAvailable(state, items);
      // Nothing changed: keep the same state so nothing re-renders.
      if (next.items.length === state.items.length && next.items.every((entry, i) => entry.item === state.items[i].item)) {
        return state;
      }
      return next;
    }
    case "PURGE_UNAVAILABLE": {
      if (state.items.every(isEntryAvailable)) {
        return state;
      }
      return keepAvailable(state, state.items);
    }
    case "DISMISS_REMOVED": {
      if (!state.removed?.length) {
        return state;
      }
      return { ...state, removed: [] };
    }
    default:
      return state;
  }
}

const CartContext = createContext({
  items: [],
  totalItems: 0,
  subtotalUsd: 0,
  addItem: () => false,
  removeItem: () => {},
  updateQuantity: () => {},
  clearCart: () => false,
  isInCart: () => false,
  refreshItemData: () => {},
  revalidateCart: async () => 0,
  removedItems: [],
  dismissRemoved: () => {},
  openCart: () => {},
  closeCart: () => {},
  toggleCart: () => {},
  isOpen: false,
  syncing: false,
  lastSyncTime: null,
  isOverlayOpen: false,
  closeOverlay: () => {},
  lastAddedItem: null,
});

function readStoredState() {
  if (typeof window === "undefined" || !window.localStorage) {
    return initialState;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return initialState;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return initialState;
    }
    const items = Array.isArray(parsed.items)
      ? parsed.items
        .map((entry) => {
          if (!entry || typeof entry !== "object") {
            return null;
          }
          if (entry.id === undefined || entry.id === null) {
            return null;
          }
          return {
            id: entry.id,
            item: entry.item ?? null,
            size: entry.size ?? null,
            color: entry.color ?? null,
            quantity: 1,
            addedAt: entry.addedAt ?? Date.now(),
          };
        })
        .filter(Boolean)
      : [];
    return keepAvailable(initialState, items);
  } catch (error) {
    console.warn("Failed to parse cart state from storage", error);
    return initialState;
  }
}

export function CartProvider({ children }) {
  const { user, authorizedFetch } = useAuth();
  const [state, dispatch] = useReducer(
    cartReducer,
    initialState,
    () => readStoredState(),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const syncInFlightRef = useRef(false);
  // revalidateCart, for loadFromServer, which is defined before it.
  const revalidateRef = useRef(null);

  const persistState = useCallback((nextState) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        items: nextState.items.map((entry) => ({
          id: entry.id,
          item: entry.item,
          size: entry.size,
          color: entry.color,
          quantity: entry.quantity,
          addedAt: entry.addedAt,
        })),
      }));
    } catch (error) {
      console.warn("Failed to persist cart state", error);
    }
  }, []);

  const syncToServer = useCallback(async (items) => {
    if (!user || !authorizedFetch) {
      return;
    }
    if (syncInFlightRef.current) {
      return;
    }
    syncInFlightRef.current = true;
    setSyncing(true);
    try {
      const serverRes = await authorizedFetch("/api/cart/");
      if (!serverRes.ok) {
        console.warn("Failed to fetch server cart");
        return;
      }
      const serverItemsRaw = await serverRes.json();
      const serverItems = Array.isArray(serverItemsRaw) ? serverItemsRaw : [];
      const serverItemMap = new Map();
      serverItems.forEach((item) => {
        const key = `${item.inventory_item_id}-${item.size || ""}-${item.color || ""}`;
        serverItemMap.set(key, item);
      });
      const localItemMap = new Map();
      items.forEach((entry) => {
        const key = `${entry.id}-${entry.size || ""}-${entry.color || ""}`;
        localItemMap.set(key, entry);
      });

      const updates = [];
      for (const [key, localEntry] of localItemMap.entries()) {
        if (!serverItemMap.has(key)) {
          updates.push(
            authorizedFetch("/api/cart/items", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                inventory_item_id: localEntry.id,
                size: localEntry.size || null,
                color: localEntry.color || null,
                quantity: localEntry.quantity,
              }),
            }),
          );
        } else {
          const serverItem = serverItemMap.get(key);
          if (serverItem.quantity !== localEntry.quantity) {
            updates.push(
              authorizedFetch(
                `/api/cart/items/${serverItem.id}?quantity=${localEntry.quantity}`,
                { method: "PATCH" },
              ),
            );
          }
        }
      }

      for (const [key, serverItem] of serverItemMap.entries()) {
        if (!localItemMap.has(key)) {
          updates.push(
            authorizedFetch(`/api/cart/items/${serverItem.id}`, {
              method: "DELETE",
            }),
          );
        }
      }

      if (updates.length > 0) {
        await Promise.all(updates);
      }
      setLastSyncTime(Date.now());
    } catch (error) {
      console.error("Failed to sync cart to server:", error);
    } finally {
      setSyncing(false);
      syncInFlightRef.current = false;
    }
  }, [authorizedFetch, user]);

  const loadFromServer = useCallback(async () => {
    if (!user || !authorizedFetch) {
      return;
    }
    try {
      const res = await authorizedFetch("/api/cart/");
      if (!res.ok) {
        return;
      }
      const serverItemsRaw = await res.json();
      const serverItems = Array.isArray(serverItemsRaw) ? serverItemsRaw : [];
      const localItems = serverItems.map((serverItem) => ({
        id: serverItem.inventory_item_id,
        item: serverItem.inventory_item || null,
        size: serverItem.size,
        color: serverItem.color,
        quantity: 1,
        addedAt: new Date(serverItem.created_at).getTime(),
      }));

      const localState = readStoredState();
      const mergedItems = [...localItems];
      localState.items.forEach((localItem) => {
        const exists = mergedItems.some(
          (item) =>
            item.id === localItem.id
            && item.size === localItem.size
            && item.color === localItem.color,
        );
        if (!exists) {
          mergedItems.push(localItem);
        }
      });
      dispatch({ type: "RESTORE_STATE", payload: { items: mergedItems } });
      setLastSyncTime(Date.now());
      // The server's copies can be stale too: check them against the database.
      setTimeout(() => revalidateRef.current?.(), 0);
    } catch (error) {
      console.error("Failed to load cart from server:", error);
    }
  }, [authorizedFetch, user]);

  const addItem = useCallback((item, size = null, color = null) => {
    if (!item || typeof item.id === "undefined" || item.id === null) {
      return false;
    }
    // Showcase items are display-only and can never be carted, whatever
    // surface the add came from (product page, brand carousel, etc.).
    if (item.showcase) {
      return false;
    }
    // Nor anything already sold or pulled: the copy on the page may be older
    // than the database, but if even it says unavailable, there is no adding.
    if (!isEntryAvailable({ item })) {
      return false;
    }
    const existing = state.items.find(
      (entry) => entry.id === item.id && entry.size === size && entry.color === color,
    );

    if (existing) {
      const normalizedEntry = { ...existing, quantity: 1 };
      dispatch({
        type: "UPDATE_QUANTITY",
        payload: {
          itemId: item.id,
          size,
          color,
          quantity: 1,
        },
      });
      setLastAddedItem(normalizedEntry);
      setIsOverlayOpen(true);
      setIsOpen(true);
      return true;
    }

    const newEntry = {
      id: item.id,
      item,
      size,
      color,
      quantity: 1,
      addedAt: Date.now(),
    };
    dispatch({ type: "ADD_ITEM", payload: item, size, color });
    setLastAddedItem(newEntry);
    setIsOverlayOpen(true);
    setIsOpen(true);
    return true;
  }, [state.items]);

  const removeItem = useCallback((itemId, size = null, color = null) => {
    if (itemId === null || itemId === undefined) {
      return;
    }
    dispatch({ type: "REMOVE_ITEM", payload: { itemId, size, color } });
    if (user && authorizedFetch) {
      const sizeParam = size ? encodeURIComponent(size) : "";
      const colorParam = color ? encodeURIComponent(color) : "";
      authorizedFetch(
        `/api/cart/items?inventory_item_id=${itemId}&size=${sizeParam}&color=${colorParam}`,
        { method: "DELETE" },
      ).catch((error) => {
        console.error("Failed to remove cart item from server:", error);
      });
    }
  }, [authorizedFetch, user]);

  const clearCart = useCallback(() => {
    if (state.items.length === 0) {
      return false;
    }
    dispatch({ type: "CLEAR_CART" });
    if (user && authorizedFetch) {
      authorizedFetch("/api/cart/", {
        method: "DELETE",
      }).catch((error) => {
        console.error("Failed to clear cart on server:", error);
      });
    }
    return true;
  }, [authorizedFetch, dispatch, state.items.length, user]);

  const isInCart = useCallback(
    (itemId, size = null, color = null) =>
      state.items.some(
        (entry) => entry.id === itemId && entry.size === size && entry.color === color,
      ),
    [state.items],
  );

  const refreshItemData = useCallback((item) => {
    if (!item || typeof item.id === "undefined" || item.id === null) {
      return;
    }
    dispatch({ type: "REFRESH_ITEM", payload: item });
  }, []);

  const itemsRef = useRef(state.items);
  useEffect(() => {
    itemsRef.current = state.items;
  }, [state.items]);

  // Revalidate cart snapshots against the database. Prices/titles/availability
  // can change at any time (Discord site manager edits the DB directly), and
  // localStorage keeps whatever was true when the item was added. Batched
  // requests (50 ids per chunk, the endpoint's cap) fired in the background —
  // rendering never waits on them. A failed chunk leaves its items untouched:
  // only ids from successful responses are treated as "requested", so a
  // network blip can never delete cart entries.
  // Resolves to how many cart entries it found no longer purchasable (and
  // removed), so a caller about to check out can stop and say so.
  const runRevalidate = useCallback(async () => {
    const ids = [...new Set(itemsRef.current.map((entry) => entry.id).filter(Boolean))];
    if (!ids.length) {
      return 0;
    }
    const chunks = [];
    for (let i = 0; i < ids.length; i += 50) {
      chunks.push(ids.slice(i, i + 50));
    }
    try {
      const results = await Promise.all(
        chunks.map(async (chunk) => {
          try {
            const res = await fetch(
              `/api/inventory/batch?ids=${chunk.map((id) => encodeURIComponent(id)).join(",")}`,
            );
            if (!res.ok) {
              return null;
            }
            const data = await res.json();
            return { chunk, items: Array.isArray(data) ? data : [] };
          } catch {
            return null;
          }
        }),
      );
      const succeeded = results.filter(Boolean);
      if (!succeeded.length) {
        return 0;
      }
      const freshItems = succeeded.flatMap((result) => result.items);
      const requestedIds = succeeded.flatMap((result) => result.chunk);
      const freshById = new Map(freshItems.filter(Boolean).map((item) => [item.id, item]));
      const requested = new Set(requestedIds);
      const lost = itemsRef.current.filter((entry) => {
        if (!requested.has(entry.id)) return false;
        const item = freshById.get(entry.id);
        return !item || !isEntryAvailable({ item });
      }).length;
      dispatch({ type: "REFRESH_ITEMS", payload: { items: freshItems, requestedIds } });
      return lost;
    } catch (error) {
      console.warn("Cart revalidation failed", error);
      return 0;
    }
  }, []);

  // Checks asked for while one is running share it (the provider, the cart
  // page and a focus event can all ask at once): one request, one answer.
  const revalidateInFlightRef = useRef(null);
  const revalidateCart = useCallback(() => {
    if (!revalidateInFlightRef.current) {
      revalidateInFlightRef.current = runRevalidate().finally(() => {
        revalidateInFlightRef.current = null;
      });
    }
    return revalidateInFlightRef.current;
  }, [runRevalidate]);
  revalidateRef.current = revalidateCart;

  const dismissRemoved = useCallback(() => dispatch({ type: "DISMISS_REMOVED" }), []);

  // Re-check while the site is open, not only on load and at checkout: when
  // the tab comes back into view or the window regains focus, and every
  // REVALIDATE_INTERVAL_MS while the tab is visible and the cart has anything
  // in it. A piece that sells in the meantime leaves the cart with a notice.
  const hasItems = state.items.length > 0;
  useEffect(() => {
    if (!hasItems) {
      return undefined;
    }
    const onFocus = () => {
      if (!document.hidden) revalidateCart();
    };
    const interval = setInterval(onFocus, REVALIDATE_INTERVAL_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [hasItems, revalidateCart]);

  const updateQuantity = useCallback((itemId, size, color) => {
    dispatch({ type: "UPDATE_QUANTITY", payload: { itemId, size, color, quantity: 1 } });
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const toggleCart = useCallback(() => setIsOpen((value) => !value), []);
  const closeOverlay = useCallback(() => setIsOverlayOpen(false), []);

  // Calculate subtotal with baked-in service fee (2.5% min $5 max $20 per item)
  // TEMPORARILY DISABLED: Baked-in fees disabled
  const subtotalUsd = useMemo(() => state.items.reduce((total, entry) => {
    const listing = entry.item ?? {};
    const baseUsd = listing?.price_usd || (listing?.price ? (typeof listing.price === 'number' ? listing.price / 100 : listing.price) : 0);
    const basePrice = parseFloat(baseUsd || 0);
    
    // Calculate service fee for this item: max(2.5% of price, $5), capped at $20
    // TEMPORARILY DISABLED: serviceFee = 0
    const serviceFee = 0; // Math.min(Math.max(basePrice * 0.025, 5.00), 20.00);
    const bakedPrice = basePrice + serviceFee;
    
    return total + (bakedPrice * entry.quantity);
  }, 0), [state.items]);

  const totalItems = useMemo(() => state.items.reduce(
    (total, entry) => total + entry.quantity,
    0,
  ), [state.items]);

  // Persist the cart state whenever it changes.
  useEffect(() => {
    persistState({ items: state.items });
  }, [state.items, persistState]);

  // Purge any sold/unavailable items on mount, then sync the survivors with
  // the database in the background.
  useEffect(() => {
    dispatch({ type: "PURGE_UNAVAILABLE" });
    revalidateCart();
  }, [revalidateCart]);

  useEffect(() => {
    if (user) {
      loadFromServer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user) {
      return;
    }
    const timeoutId = setTimeout(() => {
      syncToServer(state.items);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [state.items, user, syncToServer]);

  const contextValue = useMemo(() => ({
    items: state.items,
    totalItems,
    subtotalUsd,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    isInCart,
    refreshItemData,
    revalidateCart,
    removedItems: state.removed || [],
    dismissRemoved,
    openCart,
    closeCart,
    toggleCart,
    isOpen,
    syncing,
    lastSyncTime,
    isOverlayOpen,
    closeOverlay,
    lastAddedItem,
  }), [
    state.removed,
    dismissRemoved,
    state.items,
    totalItems,
    subtotalUsd,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    isInCart,
    refreshItemData,
    revalidateCart,
    openCart,
    closeCart,
    toggleCart,
    isOpen,
    syncing,
    lastSyncTime,
    isOverlayOpen,
    closeOverlay,
    lastAddedItem,
  ]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
